<script setup lang="ts">
    import { computed, onMounted, ref, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import { http } from '@/shared/httpWrapper';
    import Breadcrumbs from '@/shared/components/Breadcrumbs.vue';
    import QueryTableContainer from '@/shared/components/QueryTableContainer.vue';
    import { getCodeSetViewOrderBy, type CodeSetViewResultCodeRow } from '@/shared/helpers/api';
    import { createCodeDefArray, type CodeDef } from '@/shared/helpers/dataHelpers';
    import { chainComparers, comparing, flipComparer } from '@/shared/helpers/utils';
    import _ from 'lodash';
    import CodesetDisplayOptions from './CodesetDisplayOptions.vue';
    import CodeSetHeader from './CodeSetHeader.vue';
    import VirtualList from './VirtualList.vue';
    import { useCodeQueryStore } from '@/stores/codeQueryStore';
    import { canEditCode } from '@/shared/directives/requirePermission';
    import type { CodeSetProp, Library } from '@/shared/helpers/api/types/entities';
    import type { FilterFunc } from '@/stores/codeQueryStore.helper';

    const props = defineProps<{
        codeSet: CodeSetProp;
    }>();

    const emit = defineEmits<{
        rowcount: [count: number];
        refresh: [];
    }>();

    const route = useRoute();
    const codeQueryStore = useCodeQueryStore();

    const tableColumns = ref<CodeDef[]>([]);
    const tableRows = ref<CodeSetViewResultCodeRow[]>([]);
    const tableRowsFiltered = ref<CodeSetViewResultCodeRow[]>([]);
    const loading = ref(true);
    const library = ref<Library | null>(null);
    const sortCache = ref({ column: 'name', direction: 'asc' });
    const interactive = ref(true);
    const queries = ref({ regEx: [] as string[], queries: [] as string[][] });
    const exportCols = ref<string[] | null>(null);
    const libraryHeaderOpen = ref(true);
    const userCanEditCodes = ref(false);
    const editCodeId = ref<string | null>(null);

    const visibleTableColumns = computed(() => tableColumns.value.filter(c => c.visible));
    const filters = computed<FilterFunc[]>(() => codeQueryStore.filters);
    const nameAlias = computed(() => library.value ? library.value.nameAlias : '');

    watch(filters, () => {
        filterCodes();
        sortByColumn(sortCache.value.column, sortCache.value.direction);
    });

    onMounted(async () => {
        library.value = props.codeSet.library;
        await load();
    });

    async function load() {
        loading.value = true;
        await loadCodes();

        userCanEditCodes.value = await canEditCode({
            libraryName: library.value!.name,
            scopes: props.codeSet.scopes
        });

        tableColumns.value = createCodeDefArray(library.value!.attributeDefinitions).map(a => ({ ...a, visible: false }));
        codeQueryStore.setLibraryName(library.value!.name);
        codeQueryStore.addColumns(_.cloneDeep(tableColumns.value));
        emit('rowcount', tableRows.value.length);

        libraryHeaderOpen.value = !route.query.noheader;

        filterCodes();
        loading.value = false;
    }

    async function reloadDataAfterChange() {
        loading.value = true;
        await loadCodes();

        emit('rowcount', tableRows.value.length);

        loading.value = false;
        filterCodes();
        sortByColumn(sortCache.value.column, sortCache.value.direction);
    }

    function changeColumnsVisibility(callback: (_cols: CodeDef[]) => void) {
        callback(tableColumns.value);
        localStorage.setItem('Library.' + library.value!.name + '.SelectedTableColumns', tableColumns.value.filter(col => col.visible).map(col => col.field).toString());

        exportCols.value = tableColumns.value.filter(c => c.visible).map(col => {
            switch (col.field) {
                case 'name': return 'Name';
                case 'description': return 'Description';
                case 'identity': return 'Identity';
                case 'isValid': return 'IsValid';
                case 'dateUpdated': return 'DateUpdated';
                case 'dateCreated': return 'DateCreated';
                case 'attachmentKey': return 'AttachmentKey';
                case 'commonLibraryIRI': return 'CommonLibraryIRI';
                default: return col.field;
            }
        });
    }

    function changeCodeRefDisplayMode(callback: (_cols: CodeDef[]) => void) {
        callback(tableColumns.value);
        localStorage.setItem('Library.' + library.value!.name + '.ReferenceDisplayOverrides', tableColumns.value.filter(c => c.type.toUpperCase() === 'CODEREF').map(c => `${c.field}:${c.referenceDisplayMode}`).toString());
    }

    function sortByColumn(columnName: string, direction: string) {
        sortCache.value.column = columnName;
        sortCache.value.direction = direction;

        const column = tableColumns.value.find(c => c.field === columnName);
        if (!column)
            return;

        let comparer = getComparer(column);
        if (direction === 'desc') {
            comparer = flipComparer(comparer);
        }

        tableRowsFiltered.value = tableRowsFiltered.value.toSorted(comparer);
    }

    function getComparer(column: CodeDef) {
        switch (column.type.toUpperCase()) {
            case 'INT':
            case 'FLOAT':
                return comparing((a: CodeSetViewResultCodeRow) => Number(a.codeItems[column.valueIdx].value));
            case 'CODEREF':
                switch (column.referenceDisplayMode) {
                    case 'NAME':
                        return comparing((row: CodeSetViewResultCodeRow) => row.codeItems[column.valueIdx].name);
                    case 'IDENTITY':
                        return comparing((row: CodeSetViewResultCodeRow) => row.codeItems[column.valueIdx].value);
                    case 'DESCRIPTION':
                        return comparing((row: CodeSetViewResultCodeRow) => row.codeItems[column.valueIdx].description);
                    default:
                        return chainComparers(
                            comparing((row: CodeSetViewResultCodeRow) => row.codeItems[column.valueIdx].name),
                            comparing((row: CodeSetViewResultCodeRow) => row.codeItems[column.valueIdx].description)
                        );
                }
            case 'STRING':
            default:
                return comparing((a: CodeSetViewResultCodeRow) => a.codeItems[column.valueIdx].value);
        }
    }

    function filterCodes() {
        if (loading.value) return;

        if (filters.value.length) {
            tableRowsFiltered.value = [];
            for (let i = 0; i < tableRows.value.length; i++) {
                if (filters.value.every(f => f(tableRows.value[i].codeItems)))
                    tableRowsFiltered.value.push(tableRows.value[i]);
            }
        } else {
            tableRowsFiltered.value = tableRows.value.slice();
        }
    }

    async function loadCodes() {
        tableRows.value = (await getCodeSetViewOrderBy(http, props.codeSet.name, 'Name')).data.node.codeRow;
    }

    function setQueries(query: { regEx: string[]; queries: string[][] }) {
        queries.value = query;
    }

    function setInteractive(input: boolean) {
        interactive.value = input;
        if (!input) {
            editCodeId.value = null;
        }
    }

    function onEditItemRequest(item: { id: string }) {
        editCodeId.value = item.id;
    }

    async function onEditorRefresh() {
        editCodeId.value = null;
        await reloadDataAfterChange();
        emit('refresh');
    }
</script>

<template>
    <div class="is-ancestor">
        <div class="is-flex">
            <breadcrumbs
                class="mr-3"
                :code-set="codeSet"
                :library="library" />
            <b-button
                class="ml-auto has-margin-top has-margin-bottom is-text"
                :icon-right="libraryHeaderOpen ? 'menu-up' : 'menu-down'"
                @click="libraryHeaderOpen = !libraryHeaderOpen">
                {{ libraryHeaderOpen ? "Hide library header" : "Show library header" }}
            </b-button>
        </div>
        <b-collapse
            class="has-margin-bottom"
            :model-value="libraryHeaderOpen">
            <div>
                <code-set-header
                    :code-set="codeSet"
                    :filtered-code-count="tableRowsFiltered.length"
                    :code-count="tableRows.length"
                    :export-columns="exportCols"
                    :queries="queries"
                    :code-id="editCodeId"
                    @set-editor-active="setInteractive($event)"
                    @refresh="onEditorRefresh" />
                <div
                    v-show="interactive"
                    class="is-flex is-gap-10 mt-3 is-flex-wrap-wrap-reverse">
                    <div
                        class="mr-5 is-flex-grow-1">
                        <query-table-container
                            :rows="tableRowsFiltered"
                            :loading="loading"
                            :columns-def="tableColumns"
                            @queries="setQueries($event)" />
                    </div>

                    <codeset-display-options
                        v-if="!loading"
                        :library="library"
                        @column-visibility="changeColumnsVisibility($event)"
                        @coderef-display-mode="changeCodeRefDisplayMode($event)" />
                </div>
            </div>
        </b-collapse>
        <div>
            <b-loading
                v-model="loading"
                :is-full-page="false"
                :can-cancel="false" />
            <virtual-list
                :rows="tableRowsFiltered"
                :columns="visibleTableColumns"
                :name-alias="nameAlias"
                :edit-button-enabled="userCanEditCodes"
                keyfield="id"
                class="scroll-table"
                @sort="sortByColumn"
                @edit="onEditItemRequest" />
            <div v-if="tableRowsFiltered.length === 0 && !loading">
                <section class="section has-margin-top2">
                    <div class="content has-text-grey has-text-centered title is-5">
                        <p>No Items Found</p>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<style scoped>
.scroll-table {
    max-height: 70vh;
    overflow-y: auto;
    cursor: pointer;
}
</style>
